// Where the EVs charge, and what a charge costs the schedule.
//
// CommonJS so scripts/check-chargers.js can require it with no transpiler, the
// same arrangement bookingRules.js uses. Pure -- no Firestore, no Vue.
//
// ---------------------------------------------------------------------------
// Two facts from the delivery team that shape everything here
// ---------------------------------------------------------------------------
//
// 1. THE VEHICLES CHARGE FULLY AT THE DEPOT OVERNIGHT. Loading already happens
//    the evening before, so both EVs start every day at their full rangeKm.
//    A mid-route charge is therefore not a routine event -- it is what happens
//    only when the day's driving exceeds the range the car woke up with.
//
// 2. ALL TEN STATIONS THEY USE ARE DC. That is what makes 30 นาที = 70 กม.
//    credible: 70 km is roughly 14 kWh, about 28 kW sustained. A 7 kW AC post
//    would deliver about 17 km in the same half hour, and a planner assuming 70
//    would send 4ฒง-7864 to สมุทรปราการ on range that never arrived.
//
// ---------------------------------------------------------------------------
// Detour, not proximity
// ---------------------------------------------------------------------------
//
// The instinct is to ask "how far is the nearest charger". Measured that way the
// median serviceable ตำบล is ~14 km from one of these ten, which reads as a
// problem. It is the wrong question. What a route pays is the DETOUR -- the
// extra kilometres of going via the charger rather than straight past it -- and
// on every corridor the fleet drives, that is under 3.5 km and usually zero.
//
// That is not luck. Every station below is a BYD/Denza dealership or a
// Bangchak/SUSCO forecourt, which is to say every one of them sits on an
// arterial the trucks already use. Ten stations on the right roads beat a
// hundred on the wrong ones.
//
// The worst case is also the one that matters most: คลองด่าน/บางบ่อ is a 222 km
// round trip against 4ฒฎ-7073's 220 km range, so it MUST charge -- and the
// detour to do it is ~0 km.

const geo = require('./geo')
const installTime = require('./installTime')

// The stations the delivery team actually uses, given by them. They chose these
// two networks over cheaper ones for one reason: no queue. That is operational
// knowledge no public dataset carries -- OpenStreetMap has 298 charging stations
// for the whole of Thailand and 37% of them name no operator at all -- which is
// why this table is hand-maintained rather than imported.
//
// `brand` is which app the driver opens, nothing more; it does not enter any
// calculation. It is left null where the site is a Bangchak forecourt that could
// host either network.
//
// `kw` is unrecorded so far. Every station is DC, which is what the km-per-minute
// rate assumes; fill kw in if a station ever turns out to be materially slower
// and give it its own rate.
const CHARGERS = [
  { name: 'บีวายดี จินหลง ศรีนครินทร์', brand: 'SHARGE', lat: 13.699700, lng: 100.646589, dc: true, kw: null },
  { name: 'บีวายดี จินหลง รังสิต', brand: 'SHARGE', lat: 13.986439, lng: 100.624537, dc: true, kw: null },
  { name: 'บีวายดี แจ้งวัฒนะ', brand: 'SHARGE', lat: 13.907234, lng: 100.509686, dc: true, kw: null },
  { name: 'บางจาก กิ่งแก้ว', brand: null, lat: 13.703895, lng: 100.735103, dc: true, kw: null },
  { name: 'BYD HC ปทุมธานี', brand: 'SHARGE', lat: 13.997399, lng: 100.517892, dc: true, kw: null },
  { name: 'BYD SUSCO Beyond ประชาอุทิศ', brand: 'SHARGE', lat: 13.640525, lng: 100.500296, dc: true, kw: null },
  { name: 'BYD อ่อนนุช', brand: 'SHARGE', lat: 13.722020, lng: 100.706009, dc: true, kw: null },
  { name: 'Denza Harmony ศรีนครินทร์', brand: 'SHARGE', lat: 13.704332, lng: 100.645501, dc: true, kw: null },
  { name: 'ปั๊มบางจาก เสรีไทย', brand: null, lat: 13.776654, lng: 100.660937, dc: true, kw: null },
  { name: 'ปั๊มบางจาก บางบัวทอง', brand: null, lat: 13.956457, lng: 100.405409, dc: true, kw: null }
]

// 30 นาที = 70 กม., measured by the delivery team on 4ฒง-7864.
//
// Treated as linear, which is a simplification worth naming: DC charging tapers
// sharply above roughly 80% state of charge, so a SECOND half-hour in the same
// day yields materially less than the first. Because the vehicles start full
// from the depot, a single mid-route charge covers essentially every route --
// so the linear model is only used in the range it is accurate over. If two
// charges in one day ever becomes normal, this needs a taper curve, not a
// bigger number.
const CHARGE_MINUTES = installTime.CHARGE_MINUTES        // 30
const CHARGE_KM = 70
const KM_PER_MINUTE = CHARGE_KM / CHARGE_MINUTES

// Leaving the road, finding the bay, parking, plugging in, unplugging, and
// getting back on. The detour figures below come from straight-line geometry
// with the charger as a waypoint, which cannot see a divided arterial with no
// U-turn for three kilometres -- this is what pays for that.
const ACCESS_MINUTES = 8

// How far off the route a charger may sit before the planner stops treating it
// as "on the way". Above this it is cheaper to assume the driver finds
// something closer, which is what the generic fallback below is for.
const MAX_DETOUR_KM = 6

// What to assume when no known charger is near the route.
//
// The delivery team reports it takes 10-20 minutes to find a Rêver/SHARGE or
// EleXA station near a customer, and they are almost certainly right -- these
// two networks are far larger than the ten stations recorded here. So a missing
// charger must never make a route infeasible.
//
// But it must not be free either. Charging the day nothing for an unknown
// charger would let the planner build routes that only work if a station
// happens to be next door. These numbers say: assume one can be found, and
// assume it costs something to reach.
const GENERIC = {
  detourKm: 8,
  extraMinutes: 15
}

// Fraction of a battery held back rather than planned into. Road distance here
// is haversine x 1.35 and carries roughly +/-15% error, so a route planned to
// the last kilometre is a route that sometimes does not arrive.
const RESERVE_FRACTION = 0.15

// With overnight depot charging, every vehicle starts the day full. Written as
// a function rather than read as `car.rangeKm` at each call site so that the day
// this stops being true -- a charger fault, a car that came back late -- there
// is one place to change.
function startingRangeKm (car) {
  if (!car || !isFinite(car.rangeKm)) return 0
  return car.rangeKm
}

// The distance a car may actually plan to drive between charges.
function usableRangeKm (car, reserveFraction) {
  const reserve = reserveFraction == null ? RESERVE_FRACTION : reserveFraction
  return startingRangeKm(car) * (1 - reserve)
}

function kmGained (minutes) {
  if (!isFinite(minutes) || minutes <= 0) return 0
  return minutes * KM_PER_MINUTE
}

function minutesForKm (km) {
  if (!isFinite(km) || km <= 0) return 0
  return km / KM_PER_MINUTE
}

// The extra distance of going from -> charger -> to instead of from -> to.
// Never negative: a charger that appears to shorten the leg is an artefact of
// straight-line geometry, not a discovery.
function detourKm (from, to, charger) {
  const direct = geo.roadDistanceKm(from, to)
  if (direct == null || !charger) return null
  const via = geo.roadDistanceKm(from, charger)
  const back = geo.roadDistanceKm(charger, to)
  if (via == null || back == null) return null
  return Math.max(0, via + back - direct)
}

// The cheapest charger to slot into this leg, by detour rather than by distance
// from either end. Returns null when nothing is close enough to the route,
// which is the signal to fall back to GENERIC rather than to give up.
function bestChargerForLeg (from, to, options) {
  const opts = options || {}
  const limit = opts.maxDetourKm == null ? MAX_DETOUR_KM : opts.maxDetourKm
  const pool = opts.chargers || CHARGERS

  let best = null
  pool.forEach(charger => {
    const km = detourKm(from, to, charger)
    if (km == null || km > limit) return
    if (!best || km < best.detourKm) best = { charger: charger, detourKm: km }
  })
  return best
}

// What inserting a charge into this leg costs the schedule.
//
// A charge costs the full half hour, plus the access overhead and the detour,
// UNLESS it is taken over lunch -- pass `absorbedBy: 'lunch'` -- where the
// driver can eat at the station and the half hour is being spent anyway.
//
// It used to be free during a long installation too, and that was wrong. The
// driver installs: they work the job with the support staff rather than waiting
// in the vehicle, so the car stands at the customer's address for the whole
// installation and cannot be at a charger. Removing that assumption adds 30
// minutes to every non-lunch charge, which is what a charge actually costs.
function planCharge (from, to, options) {
  const opts = options || {}
  const found = bestChargerForLeg(from, to, opts)

  const known = !!found
  const charger = known ? found.charger : null
  const detour = known ? found.detourKm : GENERIC.detourKm

  // Lunch, or nothing. Reported as `absorbedBy` rather than a bare boolean
  // because the caller credits the driver's break as taken when lunch is what
  // absorbed the charge, and must not when anything else did.
  const absorbedBy = opts.absorbedBy === 'lunch' ? 'lunch' : null
  const absorbed = !!absorbedBy

  const chargeMinutes = opts.chargeMinutes == null ? CHARGE_MINUTES : opts.chargeMinutes

  // Time the day loses. The charge itself is free when it runs inside a stop the
  // driver is already parked for; the access overhead and the extra driving are
  // never free.
  const addedMinutes = ACCESS_MINUTES +
    (absorbed ? 0 : chargeMinutes) +
    (known ? 0 : GENERIC.extraMinutes)

  return {
    source: known ? 'known' : 'generic',
    charger: charger,
    name: known ? charger.name : 'หาสถานีชาร์จระหว่างทาง',
    detourKm: detour,
    chargeMinutes: chargeMinutes,
    absorbed: absorbed,
    absorbedBy: absorbedBy,
    addedMinutes: addedMinutes,
    kmGained: kmGained(chargeMinutes),
    // The detour burns range too -- a charge that gains 70 km and spends 8
    // getting there nets 62. Ignoring this is how a plan runs out one leg short.
    netKmGained: kmGained(chargeMinutes) - detour
  }
}

// How many charges a route of this length needs, given a car that started full.
// Zero for almost every route -- which is the point of charging overnight.
function chargesNeeded (routeKm, car, options) {
  const opts = options || {}
  const usable = usableRangeKm(car, opts.reserveFraction)

  // A car with no range figure is an unknown car, not a car with a flat
  // battery. Answering "one charge and it is fine" would quietly plan a
  // charging stop for a vehicle nobody has confirmed is even electric, so this
  // refuses to answer instead.
  if (usable <= 0) return Infinity

  if (!isFinite(routeKm) || routeKm <= usable) return 0

  const perCharge = kmGained(opts.chargeMinutes == null ? CHARGE_MINUTES : opts.chargeMinutes) -
    (opts.detourKm == null ? GENERIC.detourKm : opts.detourKm)

  // A charge that nets nothing can never close the gap, and looping on it would
  // hang the planner.
  if (perCharge <= 0) return Infinity

  return Math.ceil((routeKm - usable) / perCharge)
}

function findByName (name) {
  return CHARGERS.filter(c => c.name === name)[0] || null
}

module.exports = {
  CHARGERS,
  CHARGE_MINUTES,
  CHARGE_KM,
  KM_PER_MINUTE,
  ACCESS_MINUTES,
  MAX_DETOUR_KM,
  GENERIC,
  RESERVE_FRACTION,
  startingRangeKm,
  usableRangeKm,
  kmGained,
  minutesForKm,
  detourKm,
  bestChargerForLeg,
  planCharge,
  chargesNeeded,
  findByName
}
